/**
 * insurance.ts — the ONE read of a contract's insurance, and the only one.
 *
 * IT IS A SOLE OWNER ON PURPOSE. `['insurance-details', contractId]` used to be registered
 * with TWO different query functions — the setup hook's and the insurance tab's — returning
 * two different shapes for the same cache entry. React Query keeps one entry per key and the
 * first observer to mount supplies the function, so which shape the tab got depended on
 * mount order: the setup hook's flattening dropped `insurance_contract_id`, and the loan
 * INSERT then went out with `insurance_contract_id: undefined`, which supabase-js strips
 * from the body, which the NOT NULL column rejects. The loan save simply failed.
 *
 * The envelope below removes the race structurally rather than by convention. There is one
 * fetcher, and it names the three things a caller can want separately, so no caller has to
 * guess which flattening it is holding:
 *
 * - `junctionId` — `insurance_contracts.id`. INSURANCE LOANS HANG OFF THIS, not off the
 *   policy: `insurance_loans.insurance_contract_id` is the junction row, so a loan written
 *   against `policy.id` would be attached to the wrong thing entirely.
 * - `policy` — the `insurances` row (plus its premiums and discounts). `policy.id` is the
 *   POLICY id, which is what `unlink_insurance_from_contract` and `delete_insurance` take.
 * - `loans` — the junction's `insurance_loans`, with their instalments.
 * - `chainLength` — how many junctions the contract has in all, so a caller can tell the
 *   ordinary contract from one carrying a renewal chain without a second read [D-B17].
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

import { validatePolicyDraft, type PickablePolicy, type PolicyDraft } from './policyDraft';
import {
    ATTEST_PROFILE_UNKNOWN,
    certificateAccepted,
    type Attestation,
} from '@/lib/certificatePrefill';
import { readCertificateCoverPeriod } from '@/lib/certificateRead';
import type { ContractVehicle } from '@/lib/certificateVehicle';

/** The key both surfaces read and every writer invalidates. */
export const contractInsuranceKey = (contractId: number) =>
    ['contract-insurance', contractId] as const;

/** Prefix form, for writers that do not know (or care) which contract they touched. */
export const CONTRACT_INSURANCE_KEY = ['contract-insurance'] as const;

/**
 * One `insurance_loans` row with its instalments, TYPED FROM THE GENERATED SCHEMA.
 *
 * IT IS SPELLED `interest_rate`, AND AN `any[]` IS WHY THAT HAD TO BE FOUND BY READING. The
 * snapshot adapter mapped `loan.rate` — a field this table has never had — so the manifest's
 * insurance-loan row reported every financed premium as carrying no rate at all, silently,
 * because nothing in an `any` can be wrong. Deriving the row from `Tables<'insurance_loans'>`
 * rather than restating it means the next column rename fails the typecheck instead of the page.
 */
/**
 * One `insurance_loan_deposits` row with the transaction that IS the money [D-B45].
 *
 * THE LINK ROW CARRIES NO AMOUNT. `insurance_loan_deposits` is a claim that one transaction paid
 * down this loan — `loan_id`, `transaction_id` and nothing else — so every figure comes off
 * `transactions`, and a deposit whose transaction the embed did not return contributes nothing.
 * `transaction_at` is a TIMESTAMPTZ and is read through the business-day helpers wherever it is
 * printed, exactly as the premium's own date is.
 */
export type ContractInsuranceLoanDeposit = Pick<
    Tables<'insurance_loan_deposits'>,
    'id' | 'transaction_id'
> & {
    transactions: Pick<Tables<'transactions'>, 'amount' | 'transaction_at'> | null;
};

export type ContractInsuranceLoan = Tables<'insurance_loans'> & {
    insurance_loan_instalments: Tables<'insurance_loan_instalments'>[];
    insurance_loan_deposits: ContractInsuranceLoanDeposit[];
};

export interface ContractInsurance {
    /** `insurance_contracts.id` — what `insurance_loans.insurance_contract_id` points at. */
    junctionId: number;
    /** The `insurances` row. `.id` is the policy id the unlink/delete RPCs take. */
    policy: any;
    /** The junction's loans, each with its instalments. */
    loans: ContractInsuranceLoan[];
    /**
     * How many junctions this contract has in total — 1 is the ordinary contract, >1 is a
     * renewal chain.
     *
     * IT IS THE UNLIMITED TOTAL, taken from the same round trip [D-B17]. `{ count: 'exact' }`
     * makes PostgREST answer `Content-Range: 0-0/N`, where N is the size of the whole result set
     * matching the filter — PostgREST computes it BEFORE the `limit` is applied, which is the
     * only reason it survives the `LIMIT 1` this read takes — and supabase-js lifts N out of the
     * slash (`PostgrestBuilder`: `count = parseInt(contentRange[1])`). So the envelope knows the
     * chain's length without a second request and without a window in which the two answers
     * could disagree.
     *
     * WHAT IT IS FOR: the setup canvas edits the junction this contract was SET UP on, and on a
     * chain that is no longer the cover the contract is running today — so a surface that wants to
     * say so has the count without asking for it. IT GATES NOTHING. The setup canvas does not go
     * read-only above 1 (owner ruling, 2026-08-05); this is a fact about the contract, not a lock.
     */
    chainLength: number;
}

/**
 * IS THIS CONTRACT ON A RENEWAL CHAIN? [D-B17]
 *
 * IT IS DATA, NOT A LOCK — AND THAT IS THE RULING THIS DOCSTRING EXISTS TO RECORD. The setup
 * canvas briefly went read-only above 1; the owner rejected that on 2026-08-05 ("the policy should
 * be editable - no need for the error/warning message"), so `InsuranceArea` does NOT gate on this
 * predicate and no surface currently does. It stays exported, and pinned by tests, because the
 * count it reads is a genuine fact about the contract that a future surface may want to state.
 *
 * A PURE PREDICATE, AND THAT IS THE WHOLE POINT OF IT LIVING HERE. Spelled inline in JSX it was a
 * rule no test could reach — the suite runs in `environment: 'node'` and cannot mount the area.
 * One function, four cases, pinned in `contractInsuranceRead.test.ts`.
 *
 * `undefined` IS NOT A CHAIN AND NEITHER IS `null`. `null` is a contract on no policy at all —
 * legitimately, and it stays fully editable, because filling the policy block is how cover gets
 * recorded. `undefined` is the envelope still in flight, which is not a renewal either but is also
 * not a state to write in: the canvas closes writes on `isLoading` alone for that window.
 */
export function isRenewalChain(envelope: ContractInsurance | null | undefined): boolean {
    return (envelope?.chainLength ?? 1) > 1;
}

/**
 * The columns a junction envelope asks for — EXPORTED so that a second read of the same rows asks
 * for the same ones. The insurance dossier reads ONE policy on ONE contract rather than the
 * earliest junction, so it differs in its filter and in one embedded order; a second column list
 * would have been free to drift in what it selected, which is the one difference no caller could
 * see from its own file.
 */
export const INSURANCE_SELECT = `
    id,
    insurances (
        *,
        insurance_premiums(
            id,
            transaction_id,
            transactions(*),
            insurance_premium_discounts(
                id,
                transaction_id,
                transactions(id, amount, transaction_at)
            )
        )
    ),
    insurance_loans(
        *,
        insurance_loan_instalments(*),
        insurance_loan_deposits(
            id,
            transaction_id,
            transactions(amount, transaction_at)
        )
    )
`;

/**
 * A junction read narrowed to the FIRST junction the contract recorded [D-B17].
 *
 * THE RULE IS SPELLED ONCE, CLIENT-SIDE, AND IN THE SAME CHARACTERS THE SERVER USES.
 * `get_contract_setup_status` and `get_contract_agreements_status` both pick
 * `ORDER BY ic.id LIMIT 1`; every client read of a contract's junction goes through here so the
 * two sides cannot drift into grading different years of cover.
 *
 * `id` AND NOT `created_at`, deliberately: `insurance_contracts.id` is NOT NULL identity and
 * records insertion order, while `created_at` is nullable and was backfilled in one pass — it
 * records when the backfill ran, not when the cover was recorded.
 */
export function earliestJunction<T extends EarliestJunctionQuery<T>>(query: T): T {
    return query.order('id', { ascending: true }).limit(1);
}

/**
 * What `earliestJunction` needs of a PostgREST builder, and nothing more.
 *
 * Structural rather than the imported builder type: `order` and `limit` both return `this` on
 * `PostgrestTransformBuilder`, so a self-referential constraint keeps the caller's own row type
 * — and the recording stub a test hands it — flowing straight through.
 */
export interface EarliestJunctionQuery<T> {
    order(column: 'id', options: { ascending: true }): T;
    limit(count: number): T;
}

/**
 * The junction this contract was SET UP on, or null when no policy is linked.
 *
 * IT USED TO BE THE NEWEST ROW BY `created_at DESC` WITH NO TIEBREAKER, AND THAT WAS NOT A
 * PREFERENCE — IT WAS A COIN TOSS. 84 contracts in production carry a renewal chain and 73 of
 * them have every junction sharing one `created_at` (the 2026-05-11 backfill wrote the same
 * timestamp onto all of them), so on the great majority of chained contracts Postgres was free
 * to return either year's junction and this canvas printed whichever it got. The setup page,
 * the manifest's insurance-loan row and the loan editor could each be describing a different
 * policy on the same render.
 *
 * [D-B17] SETTLES IT AT THE OTHER END OF THE CHAIN: setup is the journey a contract takes
 * BEFORE the hire purchase agreement is signed, so it binds to the FIRST instance of each
 * concept — the cover the contract was activated under. Renewals are the Insurance page's, and
 * `Contract Insights` still shows the whole chain. `chainLength` comes back from the same round
 * trip so a caller can tell a renewal chain from an ordinary contract without asking again.
 */
export async function fetchContractInsurance(
    contractId: number,
): Promise<ContractInsurance | null> {
    if (!contractId) return null;
    const { data, error, count } = await earliestJunction(
        supabase
            .from('insurance_contracts')
            .select(INSURANCE_SELECT, { count: 'exact' })
            .eq('contract_id', contractId)
            /*
             * WITHIN THE JUNCTION, THE ORDER THE SERVER READS IN. `earliestJunction` settles WHICH
             * junction; these settle which row inside it, and that is a separate question with the
             * same failure mode. An embedded resource comes back in whatever order the plan
             * produced unless the request asks, and every consumer of this envelope takes the
             * FIRST of each list — `loans[0]`, the premium, that loan's instalment.
             *
             * THE TWO THAT HAVE SERVER TWINS ARE COPIED FROM THEM, character for character:
             * `get_contract_setup_status` reads these arms with `ORDER BY il.id LIMIT 1` and
             * `ORDER BY ip.id LIMIT 1` (`20260804030000_canvas_order_driver_first.sql`), so the
             * day a junction carries two loans the grade and the canvas name the same one. The
             * instalment has no twin to copy — the server only asks whether one EXISTS — so its
             * line is here for this file's own `[0]`, which is what the loan editor edits.
             *
             * THE DEPOSITS EMBED IS DELIBERATELY UNORDERED [D-B45], AND THAT IS NOT AN OMISSION.
             * The three lines below exist because a consumer takes `[0]` of the list; nothing
             * takes `[0]` of the deposits. Both questions asked of them are order-free — Σ of the
             * transaction amounts, and MAX of the transaction dates — so an `.order()` would be a
             * fourth round-trip constraint pinned by a test that asserts the whole array, bought
             * for nothing. `agreement_create` and `total_paid` sum the same rows the same way.
             *
             * THE DOSSIER'S POLICY READ DOES ORDER THEM, AND THE TWO RULES AGREE. That read binds
             * one deposit link to an editable row, so it takes `[0]` of the list and orders by `id`
             * to say WHICH one — the rule here is that a consumer summing the rows needs no order,
             * not that the deposits have none worth asking for.
             */
            .order('id', { referencedTable: 'insurance_loans', ascending: true })
            .order('id', {
                referencedTable: 'insurance_loans.insurance_loan_instalments',
                ascending: true,
            })
            .order('id', { referencedTable: 'insurances.insurance_premiums', ascending: true }),
    );
    if (error) throw error;

    const junction = data?.[0];
    if (!junction) return null;
    return {
        junctionId: junction.id,
        policy: junction.insurances as any,
        loans: (junction.insurance_loans ?? []) as unknown as ContractInsuranceLoan[],
        // A junction came back, so the chain is at least this one — the fallback is what a
        // transport that dropped the header says, never a claim that the chain is empty.
        chainLength: count ?? 1,
    };
}

export function useContractInsurance(contractId: number) {
    return useQuery({
        queryKey: contractInsuranceKey(contractId),
        queryFn: () => fetchContractInsurance(contractId),
        enabled: !!contractId,
        staleTime: 0,
    });
}

// ─── The policies a contract may be put on ───────────────────────────────────

/** `get_reusable_insurances`, under the key the selector this replaces already used. */
export const reusableInsurancesKey = (vehicleId: number, contractId: number) =>
    ['reusable-insurances', vehicleId, contractId] as const;

export async function fetchReusableInsurances(
    vehicleId: number,
    contractId: number,
): Promise<PickablePolicy[]> {
    const { data, error } = await supabase.rpc('get_reusable_insurances', {
        p_vehicle_id: vehicleId,
        p_exclude_contract_id: contractId,
    });
    if (error) throw error;
    return (data || []) as PickablePolicy[];
}

// ─── Writes ──────────────────────────────────────────────────────────────────

/**
 * POSTGRES'S OWN CLOCK, SENT AS THE VALUE — which is what `certificate_attested_at = now()` means.
 *
 * `'now'` IS A TIMESTAMPTZ INPUT LITERAL AND NOT A STRING THIS APP INVENTED: Postgres resolves it
 * to the transaction's own time as the value is cast into the column, so the day the record holds
 * is the server's day. `new Date().toISOString()` — the idiom everywhere else in this repo, and
 * harmless there — would put the ADMIN'S MACHINE on the one column whose entire job is to record
 * when a person read a document, and the standing line prints exactly that day back through
 * `businessDay`. A skewed laptop would date somebody's reading to the wrong day, on the record
 * that exists because nothing else can be checked.
 *
 * NOT A COLUMN DEFAULT, WHICH WOULD BE WRONG HERE: the pair must be settable back to NULL by the
 * save that files a readable certificate over an attested one, and a DEFAULT cannot tell the two
 * writes apart.
 */
export const SERVER_NOW = 'now';

/**
 * WHICH `profiles` ROW THIS SESSION IS, for the one column that records a person.
 *
 * `certificate_attested_by` IS A PROFILE ID AND NOT AN AUTH USER ID, because the sentence it
 * exists for names somebody — `Filed on <name>'s reading of the document on <date>` — and the name
 * lives on `profiles`. The lookup is the one `Settings` and `useContractAgreements` already make
 * for the signing profile, by `user_id`, kept here rather than reached for through a hook because
 * this runs inside a mutation and there is no React in the call stack.
 *
 * IT IS ASKED ONLY WHERE AN ATTESTATION IS ACTUALLY BEING WRITTEN, so the ordinary policy save —
 * which is every save but the handful this feature is for — costs nothing at all.
 */
export async function myProfileId(): Promise<number | null> {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return null;
    const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', auth.user.id)
        .maybeSingle();
    if (error) throw error;
    return data?.id ?? null;
}

/**
 * THE SAME LOOKUP, WHERE A FILING DEPENDS ON THE ANSWER — and it refuses rather than writing half
 * a record.
 *
 * AN ATTESTATION WITH NO AUTHOR IS NOT THE RECORD THE RULING ASKED FOR. Written as NULL, the
 * standing line reads `Filed on an admin's reading…` for ever and no later reader can find out
 * whose. The two ways the id can go missing here — no session, or a profile row this account
 * cannot see — are both the app's problem and neither is the admin's to work around, so the save
 * stops before the upload and says so in a sentence this family owns.
 *
 * THE RAW TRANSPORT ERROR IS SWALLOWED ON PURPOSE. Both save doors turn what they catch into the
 * sentence an admin reads; a PostgREST message arriving there is a refusal nobody can act on and
 * the attribution map cannot place.
 */
export async function attestingProfileId(): Promise<number> {
    let id: number | null = null;
    try {
        id = await myProfileId();
    } catch {
        id = null;
    }
    if (id === null) throw new Error(ATTEST_PROFILE_UNKNOWN);
    return id;
}

/**
 * The policy's three values and its certificate.
 *
 * THE VALIDATION SENTENCES ARE THE SHEET'S, kept word for word (`policyDraft.ts`), and the write
 * order is unchanged: upload, then either amend the `insurances` row or create one AND the
 * `insurance_contracts` junction that puts this contract on it. The junction is the gate; a policy
 * created without one is cover nothing reads.
 *
 * ONE THING NOW STANDS IN FRONT OF THE UPLOAD: a staged certificate is read and checked, and
 * anything that is not a verified Certificate of Insurance for this contract's vehicle is refused
 * before a byte is written. The refusal is the DOCUMENT'S — every other value on the block still
 * saves once the certificate is taken back out — which is why it lives here and not in
 * `validatePolicyDraft`.
 *
 * AND ONE DOCUMENT MAY NOW PASS THAT GUARD WITHOUT BEING READ AT ALL [D-B56]. A scan nobody here
 * can read may be filed on the admin's own reading of it, and `certificateAccepted` is the single
 * author of that decision for both save doors: it re-reads the bytes, and only where THAT read
 * ends on "this could not be read" does the attestation the screen collected stand. Two columns go
 * down with the upload to say so — and they are written on EVERY save that files a certificate,
 * NULL where the document reads, so an attestation can never outlive the paper it was made about.
 */
export async function savePolicyDetails({
    policyId,
    contractId,
    vehicleId,
    vehicle,
    draft,
    certificateUrl,
    certificateFile,
    attestation,
}: {
    /** The policy to amend; null creates one and links it. */
    policyId: number | null;
    contractId: number;
    vehicleId: number;
    /**
     * The plate and chassis the contract's vehicle holds, for the certificate guard below.
     * `vehicleId` is the row this policy is written against; these are what the paper is checked
     * against, and the signature carries both rather than reaching for the second from the first.
     */
    vehicle: ContractVehicle;
    draft: PolicyDraft;
    certificateUrl: string | null;
    certificateFile: File | null;
    /**
     * What the admin confirmed about a document nothing here could read, or null — which is every
     * save but the ones this feature exists for.
     */
    attestation: Attestation | null;
}): Promise<void> {
    const refusal = validatePolicyDraft(draft);
    if (refusal) throw new Error(refusal);

    let finalCertificateUrl = certificateUrl;
    /*
     * THE TWO COLUMNS THE UPLOAD CARRIES DOWN WITH IT. They stay null on a certificate the reader
     * could read, which is what CLEARS an earlier attestation the moment a readable document is
     * filed over the scan: the pair is written on every save that files a certificate, so there is
     * no path on which a stale attestation survives the paper it was about.
     */
    let attestedAt: string | null = null;
    let attestedBy: number | null = null;
    if (certificateFile) {
        /*
         * THE CERTIFICATE MUST BE A CERTIFICATE, AND IT MUST NAME THIS CONTRACT'S CAR.
         *
         * The document is READ AGAIN here rather than being handed a verdict the screen computed:
         * a disabled control is a courtesy and a piece of component state is not evidence, so the
         * only thing this can be sure of is the bytes it is about to upload. It runs BEFORE the
         * upload, so a refused certificate never reaches storage and never leaves an orphaned
         * object behind.
         *
         * IT REFUSES EVERY REJECTION AND NOT ONLY THE MISMATCH (owner's ruling, 2026-09-12): a
         * document nobody can read is not evidence of cover, so an unreadable file, an image, one
         * that is not a Certificate of Insurance, one printing neither identifier and a vehicle
         * with nothing on file to check it against are all refused here too. It stays the
         * DOCUMENT's refusal — `validatePolicyDraft` is still three sentences, and taking the
         * certificate back out still saves every other value on the block.
         *
         * WITH ONE DOOR THROUGH IT [D-B56], AND THE RE-READ IS WHAT GUARDS THAT DOOR. An
         * attestation stands only where these bytes come back unreadable AGAIN; a slot whose file
         * has been swapped for a readable one since the box was ticked is decided by the ordinary
         * rules, and a readable document naming another car is refused with the mismatch sentence
         * rather than filed on a word given about a different page.
         */
        const verdict = certificateAccepted(
            await readCertificateCoverPeriod(certificateFile),
            vehicle,
            attestation,
            certificateFile,
            { startsAt: draft.startsAt, expiresAt: draft.expiresAt },
        );
        if (verdict.refusal) throw new Error(verdict.refusal);
        if (verdict.attested) {
            attestedBy = await attestingProfileId();
            attestedAt = SERVER_NOW;
        }

        const fileExt = certificateFile.name.split('.').pop();
        const fileName = `certificates/${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
            .from('insurances')
            .upload(fileName, certificateFile);
        if (uploadError) throw uploadError;
        finalCertificateUrl = supabase.storage.from('insurances').getPublicUrl(fileName).data
            .publicUrl;
    }

    const row = {
        valued_at: parseFloat(draft.valuedAt),
        starts_at: draft.startsAt,
        expires_at: draft.expiresAt,
        certificate_url: finalCertificateUrl,
        /*
         * ONLY WHERE THIS SAVE FILED A DOCUMENT. An ordinary edit of the insured value or the
         * period stages no file, and must not wipe the attestation standing over the certificate
         * already on the row — the pair belongs to the paper, not to the policy's other values.
         */
        ...(certificateFile
            ? { certificate_attested_at: attestedAt, certificate_attested_by: attestedBy }
            : {}),
    };

    if (policyId != null) {
        const { error } = await supabase.from('insurances').update(row).eq('id', policyId);
        if (error) throw error;
        return;
    }

    const { data: created, error: insertError } = await supabase
        .from('insurances')
        .insert({ vehicle_id: vehicleId, ...row })
        .select('id')
        .single();
    if (insertError) throw insertError;

    const { error: junctionError } = await supabase
        .from('insurance_contracts')
        .insert({ insurance_id: created.id, contract_id: contractId });
    if (junctionError) throw junctionError;
}

/** Put this contract on an existing policy. */
export async function linkInsurancePolicy(contractId: number, insuranceId: number): Promise<void> {
    const { error } = await supabase
        .from('insurance_contracts')
        .insert({ insurance_id: insuranceId, contract_id: contractId });
    if (error) throw error;
}

/**
 * The premium payment.
 *
 * RECORDING IT IS WHAT TAKES THE TRANSACTION OUT OF THE UNASSIGNED QUEUE.
 * `count_transactions_by_purpose` only stops counting a transaction once something links it, so an
 * unrecorded premium sits in that queue forever and nobody ever goes back for it.
 */
export async function savePremiumLink({
    premiumId,
    insuranceId,
    transactionId,
}: {
    premiumId: number | null;
    insuranceId: number;
    transactionId: number | null;
}): Promise<number> {
    if (!transactionId) throw new Error('Select the payment made to the insurer.');

    if (premiumId != null) {
        const { error } = await supabase
            .from('insurance_premiums')
            .update({ transaction_id: transactionId })
            .eq('id', premiumId);
        if (error) throw error;
        return premiumId;
    }
    // THE ROW ID COMES BACK because the discount hangs off it. Recording a premium and its
    // discount in one commit needs the premium's id between the two writes, and a second read to
    // find a row we just created is a round trip that can also come back empty.
    const { data, error } = await supabase
        .from('insurance_premiums')
        .insert({ insurance_id: insuranceId, transaction_id: transactionId })
        .select('id')
        .single();
    if (error) throw error;
    return data.id;
}

/** The discount the insurer gave back. It hangs off the premium, never off the policy. */
export async function saveDiscountLink({
    discountId,
    premiumId,
    transactionId,
}: {
    discountId: number | null;
    /** Null means no premium is recorded yet, and a discount cannot exist without one. */
    premiumId: number | null;
    transactionId: number | null;
}): Promise<void> {
    if (!transactionId) throw new Error('Select the credit the insurer returned.');
    if (premiumId == null) throw new Error('Record the premium payment before its discount.');

    if (discountId != null) {
        const { error } = await supabase
            .from('insurance_premium_discounts')
            .update({ transaction_id: transactionId })
            .eq('id', discountId);
        if (error) throw error;
        return;
    }
    const { error } = await supabase
        .from('insurance_premium_discounts')
        .insert({ premium_id: premiumId, transaction_id: transactionId });
    if (error) throw error;
}

/**
 * A money string on its way to a `numeric` column.
 *
 * THE STRING TRAVELS, NOT THE PARSED NUMBER, and that is deliberate and unchanged from the sheet
 * this replaces: PostgREST hands a numeric literal straight to Postgres, which reads it at full
 * decimal precision, whereas a JavaScript number has already been through a binary float. Every
 * caller parses the value first to REJECT it; only its exact text is then sent. The generated
 * types say `number`, so this cast is what says "a numeric literal, not a JS number".
 */
function numericLiteral(value: string | null): number {
    return value as unknown as number;
}

/**
 * The insurance loan: Primelane paid the premium and is recovering it from the driver.
 *
 * THE LOAN HANGS OFF THE JUNCTION, NOT OFF THE POLICY. `insurance_loans.insurance_contract_id` is
 * the `insurance_contracts` row and it is NOT NULL — supabase-js drops an undefined key from the
 * body, so a loan written against a policy id would be attached to the wrong thing entirely, and
 * one written against `undefined` is rejected outright. That failure is the whole reason the
 * insurance read became an envelope.
 */
export async function saveInsuranceLoan({
    loanId,
    junctionId,
    amount,
    interestRate,
}: {
    loanId: number | null;
    junctionId: number;
    amount: string;
    interestRate: string;
}): Promise<number> {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) throw new Error('Enter a valid loan amount');
    const rate = interestRate.trim() === '' ? null : parseFloat(interestRate);
    if (rate !== null && isNaN(rate)) throw new Error('Enter a valid interest rate');

    if (loanId != null) {
        const { error } = await supabase
            .from('insurance_loans')
            .update({
                amount: numericLiteral(amount),
                interest_rate: numericLiteral(interestRate.trim() || null),
            })
            .eq('id', loanId);
        if (error) throw error;
        return loanId;
    }

    const { data, error } = await supabase
        .from('insurance_loans')
        .insert({
            insurance_contract_id: junctionId,
            amount: numericLiteral(amount),
            interest_rate: numericLiteral(interestRate.trim() || null),
        })
        .select('id')
        .single();
    if (!error) return data.id;

    // ADOPT, DO NOT DUPLICATE. `loanId` above is read from state loaded before this save began,
    // so two overlapping saves — a second tab, a panel reopened while the first was still in
    // flight — can both arrive here holding null and both insert. That is not hypothetical: it
    // put two 4,001 loans on one junction and added a whole second insurance week to contract
    // #318's weekly rate, which SUMS every insurance loan on the contract while total_value reads
    // only the first. `UNIQUE(insurance_contract_id)` refuses the second insert now, and that
    // refusal is precisely the news that another save already created the row this one intended
    // to create — so take that row and write these values into it, rather than showing an admin a
    // constraint name for a save that should simply have converged.
    if (error.code !== '23505') throw error;

    const { data: adopted, error: adoptError } = await supabase
        .from('insurance_loans')
        .update({
            amount: numericLiteral(amount),
            interest_rate: numericLiteral(interestRate.trim() || null),
        })
        .eq('insurance_contract_id', junctionId)
        .select('id')
        .single();
    if (adoptError) throw adoptError;
    return adopted.id;
}

/** The weekly recovery of that loan. */
export async function saveLoanInstalment({
    instalmentId,
    loanId,
    amount,
    startsAt,
}: {
    instalmentId: number | null;
    loanId: number;
    amount: string;
    startsAt: string;
}): Promise<void> {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) throw new Error('Enter a valid instalment amount');
    if (!startsAt) throw new Error('Select a start date');

    if (instalmentId != null) {
        const { error } = await supabase
            .from('insurance_loan_instalments')
            .update({ amount: numericLiteral(amount), starts_at: startsAt })
            .eq('id', instalmentId);
        if (error) throw error;
        return;
    }
    const { error } = await supabase
        .from('insurance_loan_instalments')
        .insert({ loan_id: loanId, amount: numericLiteral(amount), starts_at: startsAt });
    if (!error) return;

    // The same race, one table down, refused by `UNIQUE(loan_id, starts_at)`. A loan may hold
    // several rates across its life, but only one effective from any given date — and the
    // weekly-rate expression sums EVERY row sharing a loan's latest start date, so a duplicate
    // here inflates the rate exactly as a duplicate loan does. The week already exists: write
    // this amount into it.
    if (error.code !== '23505') throw error;

    const { error: adoptError } = await supabase
        .from('insurance_loan_instalments')
        .update({ amount: numericLiteral(amount) })
        .eq('loan_id', loanId)
        .eq('starts_at', startsAt);
    if (adoptError) throw adoptError;
}
